import { Link } from "react-router-dom";
import SocialLinks from "@/components/site/SocialLinks";
import { useBlogStatus } from "@/lib/useBlogStatus";

export default function Footer() {
    const year = new Date().getFullYear();
    // Blog-link verschijnt pas als er een gepubliceerd artikel is
    const { showBlog } = useBlogStatus();
    return (
        <footer
            data-testid="site-footer"
            className="relative mt-24 md:mt-40 bg-[var(--hds-sand)] border-t border-[var(--hds-line)]"
        >
            <div className="mx-auto max-w-[1440px] px-5 md:px-10 pt-16 md:pt-24 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-6">
                        <p
                            className="font-[Fraunces] text-[36px] md:text-[54px] leading-[1.02] tracking-tight text-[var(--hds-ink)]"
                            style={{ fontWeight: 300 }}
                        >
                            Klaar om je{" "}
                            <em
                                className="italic text-[var(--hds-clay)]"
                                style={{ fontWeight: 300 }}
                            >
                                blauwdruk
                            </em>{" "}
                            te lezen?
                        </p>
                        <Link
                            to="/boeken"
                            data-testid="footer-cta-boek"
                            className="inline-flex items-center gap-3 mt-8 text-[var(--hds-ink)] group"
                        >
                            <span className="inline-block w-10 h-10 rounded-full bg-[var(--hds-clay)] group-hover:bg-[var(--hds-clay-dark)] transition-colors duration-500 flex items-center justify-center text-[var(--hds-card)]">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    aria-hidden
                                >
                                    <path
                                        d="M2 7h10M8 3l4 4-4 4"
                                        stroke="currentColor"
                                        strokeWidth="1.2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </span>
                            <span className="text-[14px] tracking-wide">
                                Boek een sessie
                            </span>
                        </Link>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-[12px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-4">
                            Contact
                        </p>
                        <ul className="space-y-2 text-[14.5px]">
                            <li>
                                <a
                                    href="tel:0646090104"
                                    data-testid="footer-phone"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    06&#8209;46090104
                                </a>
                            </li>
                            <li>
                                <a
                                    href="mailto:info@humandesignstudio.nl"
                                    data-testid="footer-email"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    info@humandesignstudio.nl
                                </a>
                            </li>
                            <li>
                                <Link
                                    to="/gratis-qa"
                                    data-testid="footer-link-gratis-qa"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    Gratis Q&A
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/methodes"
                                    data-testid="footer-link-methodes"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    Methodes
                                </Link>
                            </li>
                            {showBlog && (
                                <li>
                                    <Link
                                        to="/blog"
                                        data-testid="footer-link-blog"
                                        className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                    >
                                        Blog
                                    </Link>
                                </li>
                            )}
                        </ul>
                        <div className="mt-8">
                            <p className="text-[12px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-3">
                                Volg mij
                            </p>
                            <SocialLinks testIdPrefix="footer-social" />
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-[12px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-4">
                            Juridisch
                        </p>
                        <ul className="space-y-2 text-[14.5px]">
                            <li>
                                <Link
                                    to="/privacy"
                                    data-testid="footer-link-privacy"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    Privacyverklaring
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/algemene-voorwaarden"
                                    data-testid="footer-link-av"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    Algemene voorwaarden
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/disclaimer"
                                    data-testid="footer-link-disclaimer"
                                    className="text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300"
                                >
                                    Disclaimer
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 md:mt-24 flex md:justify-start">
                    <div
                        data-testid="footer-wordmark"
                        className="flex items-baseline gap-2 select-none"
                    >
                        <span
                            aria-hidden
                            className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--hds-clay)] translate-y-[-2px]"
                        />
                        <span
                            className="font-[Fraunces] text-[22px] md:text-[28px] tracking-tight text-[var(--hds-ink)]"
                            style={{ fontWeight: 400 }}
                        >
                            Human Design{" "}
                            <em
                                className="not-italic text-[var(--hds-clay)]"
                                style={{ fontStyle: "italic", fontWeight: 300 }}
                            >
                                Studio
                            </em>
                        </span>
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-[var(--hds-line)] flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-2 text-[12px] text-[var(--hds-ink)]/55">
                    <span data-testid="footer-copyright" className="whitespace-nowrap">
                        © {year} Human Design Studio
                    </span>
                    <a
                        href="https://ghio.nl"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-credit"
                        className="whitespace-nowrap tracking-[0.12em] text-[var(--hds-ink)]/55 hover:text-[var(--hds-ink)]/80 transition-colors duration-300"
                    >
                        Ontworpen met rust en{" "}
                        <span className="text-[var(--hds-clay)]">&hearts;</span>
                    </a>
                </div>
            </div>
        </footer>
    );
}
